import React, { useState, useEffect, useCallback, memo, useMemo } from 'react';
import translations from '../utils/translations.json';
import axios from 'axios';

// Componente de slide memorizado
const TemplateSlide = memo(({ template, currentSlide, onNext, onPrev, onSelect, isSelected }) => {

    const boxClassName = `templateBox ${isSelected ? 'selected' : ''}`.trim();

    return (
        <div
            className={boxClassName}
            onClick={(e) => {
                e.stopPropagation(); // Previne propagação do evento
                onSelect(template.title);
                console.log('Selecionado:', template.title, 'isSelected:', isSelected);
            }}
        >
            <div className="templateContainer">
                <div className="slideContainer">
                    <img
                        src={template.slides[currentSlide]?.thumbnailUrl}
                        alt={`Template ${template.title}`}
                        className="templateImage"
                    />
                    <button
                        className="navButton prevButton"
                        onClick={() => onPrev(template.id)}
                    >
                        <span>‹</span>
                    </button>
                    <button
                        className="navButton nextButton"
                        onClick={() => onNext(template.id)}
                    >
                        <span>›</span>
                    </button>
                </div>
            </div>
            <div className="templateName">
                {template.name} 
            </div>
        </div>
    );
});

// Componente principal com lógica otimizada
const TemplateGrid = ({ setSelectedFormat, selectedFormat }) => {

    const language = localStorage.getItem('language') || 'pt';
    const t = (screen, key) => translations[language][screen][key];
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentSlides, setCurrentSlides] = useState({});

    // Fetch templates apenas uma vez na montagem inicial
    useEffect(() => {
        let isMounted = true;

        const fetchTemplates = async () => {
            try {
                setLoading(true);
                const response = await axios.get(process.env.REACT_APP_API_BASE_URL + '/api/templates');

                if (isMounted) {
                    const sortedTemplates = response.data.sort((a, b) => {
                        // Coloca o "Modelo PostSucesso" primeiro
                        if (a.name === "PostSucesso") return -1;
                        if (b.name === "PostSucesso") return 1;
                        return 0;
                    });

                    setTemplates(sortedTemplates);
                    
                    // Inicializa o estado dos slides atuais
                    const initialSlides = {};
                    sortedTemplates.forEach(template => {
                        initialSlides[template.id] = 0;
                    });
                    setCurrentSlides(initialSlides);
                    // Seleciona automaticamente o primeiro template apenas se nenhum estiver selecionado
                    if (sortedTemplates.length > 0 && !selectedFormat) {
                        
                        setSelectedFormat(sortedTemplates[0].title);
                    }
                }
            } catch (error) {
                console.error('Erro ao buscar templates:', error);
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchTemplates();

        return () => {
            isMounted = false;
        };
    }, []); // Removido selectedFormat das dependências para evitar re-renders

    const handleSelect = useCallback((templateName) => {
        setSelectedFormat(templateName);
    }, [setSelectedFormat]);

    // Handlers memorizados para evitar recriações desnecessárias
    const nextSlide = useCallback((templateId) => {
        setCurrentSlides(prev => {
            const currentIndex = prev[templateId];
            const template = templates.find(t => t.id === templateId);
            const totalSlides = template.slides.length;
            return {
                ...prev,
                [templateId]: (currentIndex + 1) % totalSlides
            };
        });
    }, [templates]);

    const prevSlide = useCallback((templateId) => {
        setCurrentSlides(prev => {
            const currentIndex = prev[templateId];
            const template = templates.find(t => t.id === templateId);
            const totalSlides = template.slides.length;
            return {
                ...prev,
                [templateId]: (currentIndex - 1 + totalSlides) % totalSlides
            };
        });
    }, [templates]);

    // Memoriza a lista de templates renderizada
    const templateList = useMemo(() => {
        return templates.map(template => {
            const isSelected = selectedFormat === template.title;
            
            return (
                <TemplateSlide
                    key={template.id}
                    template={template}
                    currentSlide={currentSlides[template.id]}
                    onNext={nextSlide}
                    onPrev={prevSlide}
                    onSelect={handleSelect}
                    isSelected={isSelected}
                />
            );
        });
    }, [templates, selectedFormat, currentSlides, nextSlide, prevSlide, handleSelect]);

    if (loading) {
        return <div className="templateGrid">{t('main', 'loadingNewTemplates')}</div>;
    }

    return (
        <div className="templateGrid">
            {templateList}
        </div>
    );
};

export default memo(TemplateGrid);